import { computed, ref, shallowRef, type ComputedRef, type Ref } from 'vue';

/** A registered element's stable `id`, its live `el`, and arbitrary typed metadata. */
export interface DomRegistration<T> {
  id: string;
  el: HTMLElement;
  meta: T;
}

/** A registration projected with its DOM `index`, current `active` state, and a scroll action. */
export interface DomRegistryItem<T> extends DomRegistration<T> {
  /** Position in document order (0-based). */
  index: number;
  active: boolean;
  scrollIntoView: () => void;
}

export interface DomRegistry<T> {
  /** Registered items in document order, each with `index`, `active`, and `scrollIntoView()`. */
  items: ComputedRef<DomRegistryItem<T>[]>;
  /** Id of the active item, or `null` when nothing is registered/active. */
  activeId: Readonly<Ref<string | null>>;
  /** Set the active id — e.g. from a scroll-spy `IntersectionObserver` or a click. */
  setActive: (id: string | null) => void;
  /** Register an element + metadata; returns an `unregister` fn to call on unmount. */
  register: (reg: DomRegistration<T>) => () => void;
}

/**
 * Generic ordered registry of DOM elements + typed metadata — the state half of a
 * scroll-spy / section-nav. Mirrors `createTabsRegistry`, but is keyed on live
 * elements and ordered by document position rather than registration order, and its
 * per-item action scrolls the element into view instead of selecting it.
 *
 * Intentionally observer-free: `activeId` is written from the outside (`setActive`),
 * exactly as `createTabsRegistry` signals selection through its owner. Wire an
 * `IntersectionObserver` (or click handlers) in the owning component and feed the
 * result into `setActive`. Metadata lives in a `shallowRef` so payloads (label text,
 * component defs, …) are not made deeply reactive.
 */
export function createDomRegistry<T>(opts?: {
  /** Options forwarded to `el.scrollIntoView` for the default scroll action. */
  scrollOptions?: ScrollIntoViewOptions;
  /** Replace the scroll action entirely — e.g. a smooth scroll with an active-lock. */
  onScrollIntoView?: (el: HTMLElement) => void;
}): DomRegistry<T> {
  const scrollOptions = opts?.scrollOptions ?? { behavior: 'smooth', block: 'start' };
  const scrollTo =
    opts?.onScrollIntoView ?? ((el: HTMLElement) => el.scrollIntoView(scrollOptions));

  const registrations = shallowRef<DomRegistration<T>[]>([]);
  const activeId = ref<string | null>(null);

  function register(reg: DomRegistration<T>): () => void {
    // Upsert by id: re-registering the same id (e.g. an item refreshing its meta
    // or element) replaces the prior entry in place rather than duplicating it.
    // This keeps active state intact across re-registers — only a genuine
    // unregister of the still-present entry clears it.
    registrations.value = [...registrations.value.filter((r) => r.id !== reg.id), reg];

    return () => {
      // No-op if this exact registration was already replaced by a re-register;
      // a stale closure must not remove the live entry or clear its active state.
      if (!registrations.value.includes(reg)) return;
      registrations.value = registrations.value.filter((r) => r !== reg);
      if (activeId.value === reg.id) activeId.value = null;
    };
  }

  function setActive(id: string | null): void {
    activeId.value = id;
  }

  const items = computed<DomRegistryItem<T>[]>(() => {
    // 4 === Node.DOCUMENT_POSITION_FOLLOWING — `b` comes after `a` in document order.
    // Spelled as the literal so the module carries no dependency on the `Node` DOM global.
    const sorted = [...registrations.value].sort((a, b) =>
      a.el.compareDocumentPosition(b.el) & 4 ? -1 : 1
    );
    return sorted.map((reg, index) => ({
      ...reg,
      index,
      active: reg.id === activeId.value,
      scrollIntoView: () => scrollTo(reg.el),
    }));
  });

  return { items, activeId, setActive, register };
}
